import { AxiosResponse } from "axios";
import { SET_TEMPLATES, SET_TEMPLATE, SET_ERRORMSG } from "@/store/mutation-types";
import {
  CLEAR_TEMPLATE_ISLOADING,
  COPY_TEMPLATE,
  DELETE_TEMPLATE,
  EDIT_TEMPLATE,
  GET_TEMPLATES,
  NEGATIVE_TEMPLATE,
  POSITIVE_TEMPLATE,
  REGISTER_TEMPLATE,
} from "@/store/action-types";
import { StateObject } from "@/dtos/State.dto";
import Template from "@/services/template/template";
import { Module } from "vuex";
import { EditTemplate, RegisterTemplate, GetTemplate } from "@/dtos/Template.dto";

const template: Module<any, {}> = {
  state: {
    template: {
      data: null,
      isLoading: false,
    },
    templates: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    template: (state: { template: any }) => state.template,
    templates: (state: { templates: any }) => state.templates,
  },
  mutations: {
    [SET_TEMPLATE]: (
      state: { template: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.template.data = payload.data;
      state.template.isLoading = payload.isLoading;
    },
    [SET_TEMPLATES]: (
      state: { templates: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.templates.data = payload.data;
      state.templates.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_TEMPLATE]: async ({ commit }: any, payload: any) => {
      commit(SET_TEMPLATE, { data: null, isLoading: true });

      const result: AxiosResponse = await Template.registerTemplate(payload as RegisterTemplate);

      if (result) {
        if (result.data.status) {
          commit(SET_TEMPLATE, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_TEMPLATE, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_TEMPLATE, { isLoading: false });
        return false;
      }
    },
    [GET_TEMPLATES]: async ({ commit }: any, payload: any) => {
      commit(SET_TEMPLATES, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Template.getTemplates(payload as GetTemplate);

        if (result) {
          if (result.data.status) {
            commit(SET_TEMPLATES, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_TEMPLATES, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_TEMPLATES, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_TEMPLATES, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(
            SET_ERRORMSG,
            "Request timed out. The server is taking too long to respond. Please try again later."
          );
        } else if (error.response?.status === 500) {
          commit(
            SET_ERRORMSG,
            "Server error occurred while loading templates. Please try again later."
          );
        } else if (error.response?.status === 404) {
          commit(SET_ERRORMSG, "Templates not found. Please contact support.");
        } else if (!error.response) {
          commit(
            SET_ERRORMSG,
            "Network error. Please check your internet connection and try again."
          );
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to load templates. Please try again."
          );
        }

        return false;
      }
    },
    [EDIT_TEMPLATE]: async ({ commit }: any, payload: any) => {
      commit(SET_TEMPLATE, { data: null, isLoading: true });

      const result: AxiosResponse = await Template.editTemplate(payload as EditTemplate);

      if (result) {
        if (result.data.status) {
          commit(SET_TEMPLATE, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_TEMPLATE, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_TEMPLATE, { isLoading: false });
        return false;
      }
    },
    [COPY_TEMPLATE]: async ({ commit }: any, payload: any) => {
      commit(SET_TEMPLATE, { data: null, isLoading: true });

      const result: AxiosResponse = await Template.copyTemplate(payload as EditTemplate);

      if (result) {
        if (result.data.status) {
          commit(SET_TEMPLATE, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_TEMPLATE, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_TEMPLATE, { isLoading: false });
        return false;
      }
    },
    [NEGATIVE_TEMPLATE]: async ({ commit }: any, payload: any) => {
      commit(SET_TEMPLATE, { data: null, isLoading: true });

      const result: AxiosResponse = await Template.negativeReply(payload as EditTemplate);

      if (result) {
        if (result.data.status) {
          commit(SET_TEMPLATE, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_TEMPLATE, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_TEMPLATE, { isLoading: false });
        return false;
      }
    },
    [POSITIVE_TEMPLATE]: async ({ commit }: any, payload: any) => {
      commit(SET_TEMPLATE, { data: null, isLoading: true });

      const result: AxiosResponse = await Template.positiveReply(payload as EditTemplate);

      if (result) {
        if (result.data.status) {
          commit(SET_TEMPLATE, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_TEMPLATE, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_TEMPLATE, { isLoading: false });
        return false;
      }
    },
    [DELETE_TEMPLATE]: async ({ commit }: any, payload: string) => {
      commit(SET_TEMPLATE, { data: { id: payload }, isLoading: true });

      const result: AxiosResponse = await Template.deleteTemplate(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_TEMPLATE, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_TEMPLATE, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_TEMPLATE, { isLoading: false });
        return false;
      }
    },
    [CLEAR_TEMPLATE_ISLOADING]: ({ commit }) => {
      commit(SET_TEMPLATE, { isLoading: false });
      commit(SET_TEMPLATES, { isLoading: false });
    },
  },
};

export default template;
